import { memo, useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectIsSavingWorkingProject,
  selectWorkingProjectRegistrationQuestions,
  selectWorkingProjectRegistrationSettings,
  selectWorkingProjectRegistrationSteps,
  selectWorkingProjectRequiredRegistrationQuestions,
} from '@/store/slices/project/selectors';
import { getRegistrationQuestions } from '@/store/slices/channel/selectors';
import { selectFeatureFlag } from '@/store/slices/feature-flags/selectors';
import { FeatureFlagsEnum } from '@/store/slices/feature-flags/types';
import useLoadRegistrationQuestions from '@/connection/hooks/use-load-registration-questions';
import useLoadSSOs from '../../hooks/use-load-ssos';
import { GateTypes, RegistrationTypes, SSOOptionsType } from '@/types/registration.types';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { deriveRegistrationUpdate } from '../../helpers/derive-registration-update';
import deriveActiveGateTypes from '../gating/helpers/derive-active-gate-types';
import useOnFinishSave from '@/utils/use-on-finish-save';
import ViewAudienceListsLink from '../gating/view-audience-lists-link';
import SettingsToggle from '../../../../controls/settings-toggle';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';

/**
 * The audience list (passcode_list) gate toggle for the SSO panel. It is the one gate allowed
 * to coexist with SSO (BDEV-5154): registrants must authenticate via SSO AND be a member of an
 * attached audience list (the registration service ANDs both checks).
 *
 * Deliberately NOT shared with GatingRegistrationSettings — the native panel's toggle
 * participates in the legacy multi-gate collapse (activeGates + resolveGateToggle survivor
 * selection), which doesn't apply here: under SSO the only legal gate is passcode_list, so
 * toggle-off is always "no gate".
 *
 * Mutually exclusive with SSO attribute rules (singleSignOn.rules): turning the gate ON is
 * blocked while rules exist and are manageable (AccessControlSettings blocks the reverse
 * direction). Turning the gate OFF stays possible even when rules exist so legacy/raced data
 * can be untangled.
 *
 * The platform.audience_list_members flag guard (disabledByFlag) IS intentionally duplicated
 * across this toggle and the native panel's — the enforcement gap it protects against is
 * auth-flow-independent. Its "active" operand deliberately differs per panel, tracking each
 * toggle's own displayed value: this one requires type === gated (pre-BDEV-5154 SSO projects
 * carry a forced-open type with a retained passcode_list pointer and must read OFF), while the
 * native one tracks the legacy multi-gate display, which has no type guard. Both then uphold
 * the same invariant: a toggle displaying OFF can't turn ON while the flag is off, and a
 * toggle displaying ON can always be turned OFF.
 */
const SsoAudienceListGate = memo(() => {
  useLoadRegistrationQuestions();

  const isSavingProject = useAppSelector(selectIsSavingWorkingProject);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const registrationSteps = useAppSelector(selectWorkingProjectRegistrationSteps);
  const registrationQuestions = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const requiredRegistrationQuestions = useAppSelector(selectWorkingProjectRequiredRegistrationQuestions);
  const channelRegistrationQuestions = useAppSelector(getRegistrationQuestions);

  const { saving: isSavingGatingSettings } = useOnFinishSave({ saveId: 'gating-settings' });

  // The type === gated guard matters: SSO projects authored before BDEV-5154 have
  // type=open with project_gate_type still pointing at passcode_list (the old forced-open
  // coercion kept the pointer), and the toggle must not read ON while nothing is enforced.
  const isAudienceGateActive = useMemo(
    () =>
      registrationSettings?.type === RegistrationTypes.gated &&
      deriveActiveGateTypes(registrationSettings).includes(GateTypes.passcode_list),
    [registrationSettings]
  );

  // Rules only block turning the gate on while they are actually manageable — i.e. while a
  // custom SSO provider is selected, the same condition under which AccessControlSettings
  // renders the Rules & Conditions card. Orphaned rules (provider deselected or the channel
  // SSO connection deleted) must not dead-end the toggle behind rules the admin cannot see.
  const { ssos } = useLoadSSOs();
  const selectedCustomSso = useMemo(() => {
    return ssos.find((sso: SSOOptionsType) => registrationSettings?.singleSignOn?.singleSignOns?.includes(sso.uuid));
  }, [registrationSettings?.singleSignOn?.singleSignOns, ssos]);

  const hasRules = (registrationSettings?.singleSignOn?.rules?.length ?? 0) > 0;
  const disabledByRules = hasRules && !!selectedCustomSso && !isAudienceGateActive;

  // The backend only enforces audience-list membership for this app's lists under V2 gating,
  // which the global platform.audience_list_members flag routes to. With the flag off, an
  // audience-gated event rejects every registrant (the V1 fallback reads legacy storage), so
  // turning the gate ON is blocked. Turning it OFF stays possible.
  const audienceListMembersFlagOn = useAppSelector(selectFeatureFlag(FeatureFlagsEnum.audience_list_members));
  const disabledByFlag = !audienceListMembersFlagOn && !isAudienceGateActive;

  const requirementsMap = registrationSettings?.requirements;
  const sourceBlockedDomains = registrationSettings?.blockedDomainEmails;
  const sourceAllowDomains = requirementsMap?.[GateTypes.domain_email]?.allowedDomains;
  const sourceDomainTab: 'allow' | 'block' =
    registrationSettings?.domain_gate_mode ??
    (sourceAllowDomains?.length ? 'allow' : sourceBlockedDomains?.length ? 'block' : 'allow');

  const dispatch = useAppDispatch();

  const handleToggle = useCallback(
    (_sKey: string, on: boolean) => {
      if (!registrationSettings) return;
      const update = deriveRegistrationUpdate(
        {
          registration_settings: registrationSettings,
          registration_steps: registrationSteps,
          registration_questions: registrationQuestions,
          required_registration_questions: requiredRegistrationQuestions,
        },
        {
          kind: 'gate',
          gateType: on ? GateTypes.passcode_list : null,
          domainGateMode: sourceDomainTab,
          allowDomains: sourceAllowDomains || [],
          blockDomains: sourceBlockedDomains || [],
          sharedPasscode: requirementsMap?.[GateTypes.shared_passcode]?.sharedPasscode || '',
        },
        channelRegistrationQuestions
      );
      dispatch(saveWorkingProjectByFields({ saveId: 'gating-settings', ...update }));
    },
    [
      channelRegistrationQuestions,
      dispatch,
      registrationQuestions,
      registrationSettings,
      registrationSteps,
      requiredRegistrationQuestions,
      requirementsMap,
      sourceAllowDomains,
      sourceBlockedDomains,
      sourceDomainTab,
    ]
  );

  const isDisabled = isSavingProject || isSavingGatingSettings || disabledByRules || disabledByFlag;
  const tooltipText = disabledByFlag
    ? `Audience list gating is not currently available on BrandTV.`
    : disabledByRules
      ? `Audience List can't be combined with SSO Rules & Conditions. Remove the attribute rules to enable it.`
      : '';
  return (
    <SettingsToggle
      label="Audience List"
      value={isAudienceGateActive}
      sKey={GateTypes.passcode_list.toString()}
      onToggle={handleToggle}
      disabled={isDisabled}
      tooltipText="Only allow users added to the audience tab to view"
      switchTooltipText={tooltipText}
      gap="0"
    >
      <SlideDownCard autoGrow animateOnInitialLoad={false} open={isAudienceGateActive}>
        <ViewAudienceListsLink show={true} />
      </SlideDownCard>
    </SettingsToggle>
  );
});

SsoAudienceListGate.displayName = 'SsoAudienceListGate';

export default SsoAudienceListGate;
